/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState, useEffect } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SNMPDevice, CreateSNMPDeviceRequest } from "@/api/snmp";

interface SNMPDeviceFormProps {
  device?: SNMPDevice | null;
  onSubmit: (data: CreateSNMPDeviceRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isOpen: boolean;
  defaultPollIntervalSeconds: number;
}

const emptyForm = (defaultInterval: number): CreateSNMPDeviceRequest => ({
  name: "",
  host: "",
  port: 161,
  community: "public",
  version: "2c",
  pollIntervalSeconds: defaultInterval,
  enabled: true,
});

export const SNMPDeviceForm: React.FC<SNMPDeviceFormProps> = ({
  device,
  onSubmit,
  onCancel,
  isSubmitting,
  isOpen,
  defaultPollIntervalSeconds,
}) => {
  const [formData, setFormData] = useState<CreateSNMPDeviceRequest>(
    emptyForm(defaultPollIntervalSeconds),
  );
  const [showCommunity, setShowCommunity] = useState(false);

  useEffect(() => {
    if (device) {
      setFormData({
        name: device.name,
        host: device.host,
        port: device.port,
        community: device.community || "",
        version: device.version,
        pollIntervalSeconds: device.pollIntervalSeconds,
        enabled: device.enabled,
      });
    } else if (isOpen) {
      setFormData(emptyForm(defaultPollIntervalSeconds));
    }
    setShowCommunity(false);
  }, [device, isOpen, defaultPollIntervalSeconds]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, name: formData.name.trim(), host: formData.host.trim() });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{device ? "Edit Switch" : "Add Switch"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="snmp-name">Name</Label>
            <Input
              id="snmp-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Core Switch"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label htmlFor="snmp-host">Host</Label>
              <Input
                id="snmp-host"
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                placeholder="192.168.1.1"
                required
              />
            </div>
            <div>
              <Label htmlFor="snmp-port">Port</Label>
              <Input
                id="snmp-port"
                type="number"
                value={formData.port}
                onChange={(e) =>
                  setFormData({ ...formData, port: Number(e.target.value) || 161 })
                }
                placeholder="161"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="snmp-community">Community String</Label>
            <div className="relative">
              <Input
                type={showCommunity ? "text" : "password"}
                id="snmp-community"
                value={formData.community}
                data-1p-ignore
                onChange={(e) =>
                  setFormData({ ...formData, community: e.target.value })
                }
                className="pr-10"
                placeholder={
                  device?.community === "configured"
                    ? "Community string is configured"
                    : "public"
                }
                required
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="absolute inset-y-0 right-0 h-full px-3 rounded-l-none border-0 bg-transparent hover:bg-transparent"
                onClick={() => setShowCommunity(!showCommunity)}
              >
                {showCommunity ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="snmp-version">SNMP Version</Label>
              <Select
                value={formData.version}
                onValueChange={(value) =>
                  setFormData({ ...formData, version: value as "1" | "2c" })
                }
              >
                <SelectTrigger id="snmp-version">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2c">v2c</SelectItem>
                  <SelectItem value="1">v1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="snmp-interval">Poll Interval (s)</Label>
              <Input
                id="snmp-interval"
                type="number"
                min={5}
                value={formData.pollIntervalSeconds}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    pollIntervalSeconds: Number(e.target.value) || defaultPollIntervalSeconds,
                  })
                }
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="snmp-enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, enabled: checked as boolean })
              }
            />
            <Label htmlFor="snmp-enabled" className="cursor-pointer">
              Enable polling
            </Label>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={isSubmitting} isLoading={isSubmitting}>
              {device ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
